import { forwardRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { formatIST } from '../../utils/formatters.js';
import { GP_TYPE_LABELS, GP_SUBTYPE_LABELS } from '../../utils/constants.js';

const GatePassPrint = forwardRef(({ gp }, ref) => {
  if (!gp) return null;

  const totalQty = gp.items?.reduce((s, i) => s + (i.quantity || i.netWeight || 0), 0) || 0;
  const totalCartons = gp.items?.reduce((s, i) => s + (i.cartons || 0), 0) || 0;

  return (
    <div ref={ref} className="p-8 bg-white text-sm font-sans" style={{ width: '210mm', minHeight: '297mm' }}>
      {/* Header */}
      <div className="border-b-2 border-gray-800 pb-4 mb-4 flex items-start justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">SREERAGAM EXPORTS PVT. LTD.</h1>
          <p className="text-base font-semibold text-gray-700 mt-0.5">{GP_TYPE_LABELS[gp.type]} Gate Pass</p>
          {gp.subType && <p className="text-sm text-gray-500">{GP_SUBTYPE_LABELS[gp.subType] || gp.subType}</p>}
        </div>
        <div className="text-right">
          <QRCodeSVG value={gp.number} size={80} />
          <p className="text-xs text-gray-500 mt-1 font-mono">{gp.number}</p>
        </div>
      </div>

      {/* Meta grid */}
      <div className="grid grid-cols-2 gap-x-8 gap-y-1 mb-4 text-sm">
        <Field label="Gate Pass No." value={gp.number} bold />
        <Field label="Date & Time" value={formatIST(gp.createdAt)} />
        <Field label="Type" value={GP_TYPE_LABELS[gp.type]} />
        <Field label="Status" value={gp.status} />
        <Field label="Source Location" value={`${gp.sourceLocation?.code} — ${gp.sourceLocation?.name}`} />
        <Field label="Destination" value={gp.destinationText || gp.destinationPPC?.name || '—'} />
        <Field label="Owner" value={gp.ownerType === 'MERCHANT_EXPORTER' ? gp.merchantExporter?.name : 'Sreeragam Exports Pvt. Ltd.'} />
        <Field label="Vehicle No." value={gp.vehicleNumber || '—'} />
        <Field label="Driver" value={gp.driverName || '—'} />
        <Field label="Driver Contact" value={gp.driverContact || '—'} />
      </div>

      {/* Export details */}
      {gp.type === 'OUT_FP' && (gp.freightForwarder || gp.containerNumber) && (
        <div className="border rounded p-3 mb-4 grid grid-cols-2 gap-x-8 gap-y-1 text-sm">
          <p className="col-span-2 font-semibold text-gray-700 mb-1">Export Details</p>
          <Field label="Freight Forwarder" value={gp.freightForwarder || '—'} />
          <Field label="Shipping Line" value={gp.shippingLine || '—'} />
          <Field label="Vessel" value={gp.vesselName || '—'} />
          <Field label="Container No." value={gp.containerNumber || '—'} />
          <Field label="BL Reference" value={gp.blReference || '—'} />
        </div>
      )}

      {/* Items table */}
      <table className="w-full border-collapse border border-gray-400 text-xs mb-4">
        <thead>
          <tr className="bg-gray-100">
            <th className="border border-gray-400 px-2 py-1 text-left">#</th>
            <th className="border border-gray-400 px-2 py-1 text-left">Species / Product</th>
            <th className="border border-gray-400 px-2 py-1 text-left">Grade</th>
            <th className="border border-gray-400 px-2 py-1 text-left">Form</th>
            <th className="border border-gray-400 px-2 py-1 text-right">Qty (kg)</th>
            <th className="border border-gray-400 px-2 py-1 text-right">Cartons</th>
            <th className="border border-gray-400 px-2 py-1 text-left">Lot No.</th>
          </tr>
        </thead>
        <tbody>
          {gp.items?.map((item, i) => (
            <tr key={item.id}>
              <td className="border border-gray-300 px-2 py-1">{i + 1}</td>
              <td className="border border-gray-300 px-2 py-1">{item.species || item.productName || item.itemDescription || '—'}</td>
              <td className="border border-gray-300 px-2 py-1">{item.grade || '—'}</td>
              <td className="border border-gray-300 px-2 py-1">{item.form || '—'}</td>
              <td className="border border-gray-300 px-2 py-1 text-right">{item.quantity != null ? item.quantity.toLocaleString() : (item.netWeight?.toLocaleString() || '—')}</td>
              <td className="border border-gray-300 px-2 py-1 text-right">{item.cartons || '—'}</td>
              <td className="border border-gray-300 px-2 py-1 font-mono">{item.lotNumber || '—'}</td>
            </tr>
          ))}
          {/* Totals row */}
          <tr className="bg-gray-50 font-semibold">
            <td className="border border-gray-400 px-2 py-1" colSpan={4}>TOTAL</td>
            <td className="border border-gray-400 px-2 py-1 text-right">{totalQty.toLocaleString()}</td>
            <td className="border border-gray-400 px-2 py-1 text-right">{totalCartons}</td>
            <td className="border border-gray-400 px-2 py-1" />
          </tr>
        </tbody>
      </table>

      {/* Attachments list */}
      {gp.attachments?.length > 0 && (
        <div className="mb-4 text-xs">
          <p className="font-semibold mb-1">Attached Files ({gp.attachments.length})</p>
          <ul className="list-disc list-inside text-gray-600 columns-2">
            {gp.attachments.map(f => <li key={f.id}>{f.originalName}</li>)}
          </ul>
        </div>
      )}

      {/* Remarks */}
      {gp.remarks && (
        <div className="mb-4 p-2 border border-gray-300 rounded text-xs">
          <span className="font-semibold">Remarks: </span>{gp.remarks}
        </div>
      )}

      {/* Approval chain */}
      <div className="mb-6 text-xs">
        <p className="font-semibold mb-1 text-gray-700">Approval Chain</p>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <p className="text-gray-500">Created By</p>
            <p className="font-medium">{gp.createdBy?.name}</p>
            <p className="text-gray-400">{formatIST(gp.createdAt)}</p>
          </div>
          {gp.approvedBy && (
            <div>
              <p className="text-gray-500">Approved By</p>
              <p className="font-medium">{gp.approvedBy?.name}</p>
              <p className="text-gray-400">{formatIST(gp.approvedAt)}</p>
            </div>
          )}
          {gp.securityOut && (
            <div>
              <p className="text-gray-500">Gate Out</p>
              <p className="font-medium">{gp.securityOut?.name}</p>
              <p className="text-gray-400">{formatIST(gp.actualDispatchAt)}</p>
            </div>
          )}
        </div>
      </div>

      {/* Signature boxes */}
      <div className="grid grid-cols-2 gap-8 mt-8">
        <SignBox label="Gate Out — Security" />
        <SignBox label="Gate In — Security" />
      </div>
      <div className="grid grid-cols-2 gap-8 mt-6">
        <SignBox label="Dispatcher / Sender" />
        <SignBox label="Receiver / Consignee" />
      </div>

      <p className="text-xs text-gray-400 text-center mt-8">
        Sreeragam Exports Pvt. Ltd. · Gate Pass System · Printed on {formatIST(new Date())}
      </p>
    </div>
  );
});

GatePassPrint.displayName = 'GatePassPrint';
export default GatePassPrint;

function Field({ label, value, bold }) {
  return (
    <div className="flex gap-1">
      <span className="text-gray-500 min-w-28">{label}:</span>
      <span className={bold ? 'font-bold' : 'font-medium'}>{value}</span>
    </div>
  );
}

function SignBox({ label }) {
  return (
    <div className="border border-gray-400 rounded p-3">
      <p className="text-xs text-gray-600 mb-8">{label}</p>
      <div className="border-b border-gray-400 mb-1" />
      <p className="text-xs text-gray-400">Name / Signature / Date</p>
    </div>
  );
}
